import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Query } from 'react-apollo';
import gql from 'graphql-tag';
import styled from 'styled-components';
import Button from '../styles/Button';
import icons from '../styles/mixin/icons';

import device from '../../constants/device';
import { MAP_CHANNEL } from '../../constants/app';

const FILTERS_QUERY = gql`
    query Filters($channel: String!) {
        map {
            filters(channel: $channel) {
                name
                category
                fields {
                    name
                    field
                    value
                }
            }
        }
    }
`;

const Wrapper = styled.div`
    background-color: #fff;
    border-radius: ${props => props.theme.metrics.radius};
    border: 1px solid ${props => props.theme.colors.lighttaupe};
    position: absolute;
    top: 10px;
    left: ${props => (props.fullscreen ? '10px' : '-100%')};
    bottom: 10px;
    width: 276px;
    max-width: 30%;
    padding: 5px;
    z-index: 99;
    transition: all 300ms;

    &::before {
        border-radius: ${props => props.theme.metrics.radius};
        border: 2px solid ${props => props.theme.colors.lighttaupe};
        bottom: 3px;
        content: '';
        display: block;
        left: 3px;
        position: absolute;
        right: 3px;
        top: 3px;
        z-index: 0;
    }

    @media ${device.mobile} {
        left: ${props => (props.collapsed ? '0' : '10px')};
        top: ${props => (props.collapsed ? '0' : '10px')};
        opacity: ${props => (props.fullscreen ? 1 : 0)};
        height: ${props => (props.collapsed ? '100%' : '44px')};
        width: ${props => (props.collapsed ? '100%' : 'auto')};
        max-width: none;
        padding: ${props => (props.collapsed ? '0' : '5px')};
        border-radius: ${props => (props.collapsed ? 0 : props.theme.metrics.radius)};
        border-width: ${props => (props.collapsed ? '0px' : '1px')};
        overflow: ${props => (props.collapsed ? 'auto' : 'hidden')};
        &::before {
            opacity: ${props => (props.collapsed ? 0 : 1)};
        }
    }
`;

const Container = styled.div`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    cursor: auto;
    font-size: ${props => props.theme.font.size.zeta};
    line-height: 1.5;
    width: 100%;
    height: 100%;
    z-index: 1;
    transition: all 300ms;

    @media ${device.mobile} {
        padding: 0;
        width: 100%;
        height: ${props => (props.collapsed ? 'auto' : '40px')};
        // overflow: ${props => (props.collapsed ? 'auto' : 'hidden')};
    }
`;

const Filters = styled.div`
    width: 100%;
    height: 100%;
    max-height: calc(100% - 69px);
    overflow-y: auto;
    overflow-x: hidden;

    @media ${device.mobile} {
        display: ${props => (props.collapsed ? 'block' : 'none')};
        margin: 10px 0 64px;
        max-height: none;
    }
`;

const FilterBarTitle = styled.h4`
    font-size: 18px !important;
    flex: 1 100%;
    padding: 20px 16px;
    font-weight: 600;
    button {
        padding: 0;
    }
    @media ${device.mobile} {
        display: none;
    }
`;

const FilterButton = styled(FilterBarTitle)`
    display: none;
    @media ${device.mobile} {
        display: block;
        padding: 4px 10px;
    }
`;

const Category = styled.div`
    position: relative;
    flex: 1 100%;
    background: ${props => (props.active ? props.theme.colors.xlighttaupe : '#fff')};
    margin: ${props => (props.active ? '0 -2px' : '0')};
    padding: ${props => (props.active ? '0 18px' : '0 16px')};
    z-index: 1;
    overflow: hidden;
    max-height: ${props => (props.active ? '500px' : '53px')};

    transition: max-height ${props => (props.active ? '1000ms' : '500ms')}, background 600ms;

    &::before,
    &::after {
        content: '';
        opacity: ${props => (props.active ? '1' : '0')};
        position: absolute;
        top: 0;
        left: 0;
        width: 2px;
        bottom: 0;
        background: ${props => props.theme.colors.taupe};
        z-index: 1;
    }
    &::after {
        left: auto;
        right: 0;
    }

    @media ${device.mobile} {
        margin: 0;
        padding: 0 15px;
        &::before,
        &::after {
            display: none;
        }
    }
`;

const CategoryWithSubfilter = styled.div`
    position: relative;
    z-index: 0;

    &:hover {
        &::before,
        &::after {
            background: ${props => props.theme.colors.red};
        }
    }

    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 25px;
        right: 0;
        width: 8px;
        height: 2px;
        background: ${props => (props.active ? props.theme.colors.red : props.theme.colors.brown)};
        z-index: -1;
        transform: ${props => (props.active ? 'rotate(45deg)' : 'rotate(-45deg)')};
        transition: transform 300ms;
    }
    &::after {
        transform: ${props => (props.active ? 'rotate(-45deg)' : 'rotate(45deg)')};
        right: 5px;
    }
`;

const Filter = styled.div`
    display: flex;
    flex-wrap: wrap;
    cursor: pointer;
    line-height: 1.5;
    padding: 13px 0 12px;
    border-bottom: 1px solid ${props => props.theme.colors.lighttaupe};
    color: ${props => (props.active ? props.theme.colors.red : props.theme.colors.brown)};

    &:hover {
        color: ${props => props.theme.colors.red};
    }
`;

const FilterIcon = styled.div`
    position: relative;
    width: 20px;
    margin-right: 10px;
    &::before {
        ${icons}
        content: ${props => props.theme.icons[`category-${props.category}`] || props.theme.icons[`category-fallback`]};
        font-size: ${props => props.theme.font.size.delta};
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

const FilterTitle = styled.div`
    flex: 1;
    padding: 0 16px 0 0;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const SubFilterList = styled.ul`
    margin: 0;
    padding: 4px 0 13px;
    list-style: none;
    &:first-child {
        margin-top: 10px;
    }
`;

const SubFilter = styled.li`
    position: relative;
    font-size: ${props => props.theme.font.size.zeta};
    cursor: pointer;
    padding: 0 0 0 28px;
    margin: 0;
    line-height: 2;

    &:first-child {
        margin-top: 10px;
    }

    &::before {
        content: '';
        position: absolute;
        display: block;
        top: 6px;
        left: 0;
        width: 16px;
        height: 16px;
        margin-right: 10px;
        border-radius: 10px;
        border: 1px solid ${props => props.theme.colors.taupe};
        vertical-align: middle;
    }

    &::after {
        content: '';
        position: absolute;
        display: block;
        top: 9px;
        left: 3px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: ${props => props.theme.colors.red};
        opacity: ${props => (props.active ? '1' : '0')};
    }

    &:hover {
        color: ${props => props.theme.colors.red};
        &::after {
            opacity: 1;
        }
    }
`;

const CloseButton = styled(Button)`
    display: none;
    @media ${device.mobile} {
        display: block;
        align-self: flex-end;
        margin: 10px;
        color: ${props => props.theme.colors.brown};
    }
`;

const SaveButton = styled(CloseButton)`
    @media ${device.mobile} {
        position: fixed;
        bottom: 10px;
        left: 10px;
        right: 10px;
        width: calc(100% - 20px);
        color: #fff;
        margin: 0;
    }
`;
// Add default filter field to array if fields array is empty
const handleFilterField = filter =>
    !filter.fields.length
        ? {
              ...filter,
              fields: [
                  {
                      name: 'Alles',
                      type: 'category',
                      value: filter.category,
                  },
              ],
          }
        : filter;

class FiltersControl extends Component {
    state = {
        isCollapsed: false,
    };

    toggleCollapse = () => this.setState(state => ({ isCollapsed: !state.isCollapsed }));

    render() {
        const {
            selected: { category: selectedCategory, subCategory },
            onClick: setSelectedFilters,
            fullscreen,
            title,
            openButton,
            closeButton,
            saveButton,
        } = this.props;
        const { isCollapsed } = this.state;

        return (
            <Query query={FILTERS_QUERY} variables={{ channel: MAP_CHANNEL }}>
                {({ data, error, loading }) => {
                    if (loading) return <p style={{ position: 'absolute' }}>Loading...</p>;
                    if (error) return console.error(error.message) || null;
                    const filters = data.map.filters.map(handleFilterField);
                    return (
                        <Wrapper collapsed={isCollapsed} fullscreen={fullscreen}>
                            <Container collapsed={isCollapsed}>
                                <FilterBarTitle>{title}</FilterBarTitle>
                                {!isCollapsed ? (
                                    <FilterButton type="button" onClick={this.toggleCollapse}>
                                        {openButton}
                                    </FilterButton>
                                ) : (
                                    <CloseButton type="button" onClick={this.toggleCollapse}>
                                        {closeButton}
                                    </CloseButton>
                                )}
                                <Filters collapsed={isCollapsed}>
                                    {filters.map(({ name, fields, category }) => {
                                        const catIsActive = category === selectedCategory;
                                        const hasSubFilters = fields.length > 1;
                                        return (
                                            <Category key={name} active={catIsActive}>
                                                {!hasSubFilters ? (
                                                    <Filter
                                                        onClick={() => setSelectedFilters({ category })}
                                                        active={catIsActive}
                                                        data-qe={name}
                                                    >
                                                        <FilterIcon category={category} />
                                                        <FilterTitle>{name}</FilterTitle>
                                                    </Filter>
                                                ) : (
                                                    <>
                                                        <CategoryWithSubfilter active={catIsActive}>
                                                            <Filter
                                                                onClick={() =>
                                                                    setSelectedFilters(catIsActive ? {} : { category })
                                                                }
                                                                active={catIsActive}
                                                                data-qe={name}
                                                            >
                                                                <FilterIcon category={category} />
                                                                <FilterTitle>{name}</FilterTitle>
                                                            </Filter>
                                                        </CategoryWithSubfilter>
                                                        <SubFilterList>
                                                            {fields.map(({ name: subname, field: type, value }) => {
                                                                const subCatIsActive =
                                                                    (!!subCategory &&
                                                                        type === subCategory.type &&
                                                                        value === subCategory.value) ||
                                                                    (!subCategory &&
                                                                        type === 'category' &&
                                                                        value === selectedCategory);

                                                                const selected =
                                                                    type !== 'category'
                                                                        ? { subCategory: { type, value } }
                                                                        : null;
                                                                return (
                                                                    <SubFilter
                                                                        key={value}
                                                                        onClick={() =>
                                                                            setSelectedFilters({
                                                                                category,
                                                                                ...selected,
                                                                            })
                                                                        }
                                                                        active={subCatIsActive}
                                                                    >
                                                                        {subname}
                                                                    </SubFilter>
                                                                );
                                                            })}
                                                        </SubFilterList>
                                                    </>
                                                )}
                                            </Category>
                                        );
                                    })}
                                </Filters>

                                {isCollapsed && (
                                    <SaveButton type="button" variant="primary" onClick={this.toggleCollapse}>
                                        {saveButton}
                                    </SaveButton>
                                )}
                            </Container>
                        </Wrapper>
                    );
                }}
            </Query>
        );
    }
}

export default FiltersControl;

FiltersControl.propTypes = {
    selected: PropTypes.shape({
        category: PropTypes.string,
        subCategory: PropTypes.shape({
            type: PropTypes.string,
            value: PropTypes.string,
        }),
    }),
    onClick: PropTypes.func.isRequired,
    fullscreen: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    openButton: PropTypes.string.isRequired,
    closeButton: PropTypes.string.isRequired,
    saveButton: PropTypes.string.isRequired,
};
